import { Dispatch, SetStateAction } from 'react';

import type { CurrentLevel } from '@/pages/freePlay/types';
import type { ViewMode } from '@/playlist/Constants';
import type { UserData } from '@/shared/types';

export type { CurrentLevel, ViewMode };

// ============================================================================
// Answer Types
// ============================================================================

export interface Answer {
	id: string | number;
	is_correct: boolean;
	answer_text?: string;
	answer_display: string;
	answer_variations?: { answer_text: string }[];
	blank_index?: number;
}

export interface UserAnswer {
	answer_text: string;
	is_correct: boolean;
	question_id: number;
}

// ============================================================================
// Question Types
// ============================================================================

export type QuestionDisplay = string | (string | Record<string, unknown>)[];

export interface InstructionStep {
	id?: string;
	message?: string | null;
	audio_url?: string;
	isViewed?: boolean;
	isFeedbackMessage?: boolean;
}

export interface QuestionInstructions {
	isOnboarding?: boolean;
	isOnboardingCheck?: boolean;
	isFeedback?: boolean;
	step_number?: number;
	instructions: InstructionStep[];
	instructionsAudioOn?: InstructionStep[];
	instructionsAudioOff?: InstructionStep[];
}

export interface Question {
	id: number;
	type: string;
	title: string;
	topic_id: number;
	topic_title?: string;
	instruction?: Record<string, any> | null;
	answers: Answer[];
	question_display: QuestionDisplay;
	question_instructions?: QuestionInstructions[];
	user_answers?: UserAnswer[];
	ai_tutor_enabled?: boolean;
	explanation_available?: boolean;
	time_estimate_in_seconds?: number;
	decoy_letters?: string[];
}

/** Question with client-side runtime properties mutated during an exercise session. */
export interface CurrentQuestion extends Question {
	isAnswerCorrect?: boolean;
	isAnswered?: boolean;
	isQuestionInstructionViewed?: boolean;
}

// ============================================================================
// Exercise Types
// ============================================================================

export interface ExerciseTypes {
	isDictateType?: boolean;
	isOpenEndedType?: boolean;
	isInYourMindType?: boolean;
	isMultipleChoiceType?: boolean;
	isTheBlanksType?: boolean;
	isOpenMultipleType?: boolean;
	isDictateMultipleType?: boolean;
	isMultipleChoiceMultipleAnswersType?: boolean;
}

export type PreparedMultipleAnswers = Record<string, any>;

// ============================================================================
// Game Types
// ============================================================================

export interface GameConfig {
	type: string;
	total_time_in_seconds: number;
	reward_attribute: string;
	correct_answer_streak: number;
	high_score?: number;
	reward: {
		correct_answer_streak: Record<number, number>;
		wrong_answer_streak: Record<number, number>;
	};
}

export enum EndOutcome {
	SUCCEEDED = 'succeeded',
	LOST = 'lost',
}

// ============================================================================
// Playlist Types
// ============================================================================

export interface PlaylistSectionText {
	title: string | null;
	text: string | null;
}

export interface PlayList {
	id: number;
	correct_answers_count: number;
	difficulty_level: number;
	free_play_theme: string;
	image: string;
	is_intervention_section: boolean;
	is_mistake: boolean;
	playlist_section_text: PlaylistSectionText;
	questions: Question[];
	questions_count: number;
	relative_difficulty: string;
	section_type: string;
	title: string;
	instruction?: Record<string, any> | null;
	topic_subcategory_id: number | null;
	topic_subcategory_name: string;
	wrong_answers_count: number;
	total_time: number;
	game?: GameConfig;
	date_played?: string | null;
}

// ============================================================================
// State Types
// ============================================================================

export interface GameSubState {
	type: string | null;
	totalTimeInSeconds: number;
	currentLevel: CurrentLevel | null;
	highScore: number | null;
	rewardAttribute: string | null;
	correctAnswerStreak: number;
	reward: {
		correctAnswerStreak: Record<number, number>;
		wrongAnswerStreak: Record<number, number>;
	} | null;
}

export interface ExerciseSubState {
	audioOn: boolean;
	instructionAudioOn: boolean;
	isReadingMode: boolean;
	readingProgress: number;
	readingTextSize: number;
	showTopicInstruction: boolean;
	showQuestionInstruction: boolean;
	showInstructionHint: boolean;
	showPulsatingHighlight: boolean;
	showReadingTitle: boolean;
	showAITutorChat: boolean;
	aiTutorFeedbackOpen: boolean;
	aiTutorFeedbacks: Record<string, any>;
	instructionFeedbacks: Record<string, boolean>;
	questionsExplanations: Record<string, any>;
	exerciseTypes: ExerciseTypes;
	instruction: Record<string, any> | null;
}

export interface PlaylistState {
	// Core playlist data
	id: string | number | null;
	playList: PlayList | null;
	questions: Question[];
	loading: boolean;
	loadingAnswer: boolean;
	loadingFinish: boolean;

	// Scoring
	correctCount: number;
	incorrectCount: number;
	questionsLeftCount: number;
	correctStreak: number;

	// Current question
	currentQuestion: Question | null;

	// Metadata
	datePlayed: string | null;
	sectionType: string | null;
	topicTitle: string | null;
	topicSubcategoryName: string | null;
	playlistSectionText: PlaylistSectionText;
	freePlayTheme: string | null;
	viewMode: ViewMode;
	skipAnswerRequests: boolean;
	skipFinishRequest: boolean;
	isBridgePlaylist: boolean;
	isPersonalityQuiz: boolean;
	personalityQuizAnswerIds: number[];

	// Game-specific (nested)
	game: GameSubState;

	// Exercise-specific UI (flattened for backward compatibility)
	audioOn: boolean;
	instructionAudioOn: boolean;
	isReadingMode: boolean;
	readingProgress: number;
	readingTextSize: number;
	showTopicInstruction: boolean;
	showQuestionInstruction: boolean;
	showInstructionHint: boolean;
	showPulsatingHighlight: boolean;
	showReadingTitle: boolean;
	showAITutorChat: boolean;
	aiTutorFeedbackOpen: boolean;
	aiTutorFeedbacks: Record<string, any>;
	instructionFeedbacks: Record<string, boolean>;
	questionsExplanations: Record<string, any>;
	exerciseTypes: ExerciseTypes;
	instruction: Record<string, any> | null;

	// Completion
	endscreen: string | null;
	levelUp: any;
	mistakeInterventionId: string | number | null;
	loadingMistakeIntervention: boolean;

	// Legacy snake_case aliases for backward compatibility
	section_type: string | null;
	topic_title: string | null;
	topic_subcategory_name: string | null;
	playlist_section_text: PlaylistSectionText;
	is_mistake: boolean;
	speech_locale: string | null;
}

// ============================================================================
// Action Types
// ============================================================================

export interface PlaylistActionPayload {
	// Core data
	id?: string | number;
	playList?: PlayList;
	data?: any;
	questions?: Question[];

	// Scoring
	correctCount?: number;
	incorrectCount?: number;
	questionsLeftCount?: number;
	correctStreak?: number;
	isCorrect?: boolean;

	// Current question
	currentQuestion?: Question;

	// Request parameters
	playlistId?: number | string;
	freePlayCategoryId?: number | string;
	questionIds?: number[];

	// View mode and UI
	viewMode?: string;
	skipAnswerRequests?: boolean;
	skipFinishRequest?: boolean;
	isBridgePlaylist?: boolean;
	audioOn?: boolean;
	instructionAudioOn?: boolean;
	exerciseType?: string;
	showTopicInstruction?: boolean;
	showQuestionInstruction?: boolean;
	showInstructionHint?: boolean;
	showPulsatingHighlight?: boolean;
	isReadingMode?: boolean;
	readingProgress?: number;
	readingTextSize?: number;
	showReadingTitle?: boolean;

	// Instruction feedback
	isUseful?: boolean;
	instructionId?: string | number;
	questionId?: string | number;
	feedback?: string;

	// Answer
	lastQuestion?: boolean;
	callback?: (response?: any) => void;
	playtimeInSeconds?: number;

	// Completion
	endscreen?: any;
	levelUp?: any;
	gameOutcome?: EndOutcome;
	score?: number;

	// Mistake intervention
	mistakeInterventionId?: string | number | null;

	// AI Tutor
	aiTutorFeedbackOpen?: boolean;

	// Personality quiz
	isPersonalityQuiz?: boolean;
	answerId?: number;

	// Game-specific
	currentLevel?: CurrentLevel;

	// Question explanations
	questionsExplanations?: Record<string, any>;

	// Audio preferences
	audioOnboarded?: boolean;
}

export interface PlaylistAction {
	type: string;
	payload?: PlaylistActionPayload;
}

// ============================================================================
// Answer Request Types
// ============================================================================

export interface SendAnswerRequestParams {
	playlistId: number | string;
	freePlayCategoryId?: number | string | null;
	data: Record<string, unknown>;
	lastQuestion?: boolean;
	callback?: (response?: any) => void;
	playtimeInSeconds?: number;
	isCorrect?: boolean;
}

export type SendAnswerRequest = (params: SendAnswerRequestParams) => void;

// ============================================================================
// Hook Types
// ============================================================================

export interface UseSecondChanceProps {
	userData: UserData;
	currentQuestion: CurrentQuestion;
	exerciseTypes: ExerciseTypes;
	viewMode: ViewMode;
	preparedMultipleAnswers: PreparedMultipleAnswers;
	setPreparedMultipleAnswers: Dispatch<SetStateAction<PreparedMultipleAnswers>>;
	sendEventRequest: (payload: { event_name: string; meta_data: Record<string, any> }) => void;
	handleUserAnswer: (question: CurrentQuestion) => void;
}

export interface UseSecondChanceResult {
	showSecondChance: boolean;
	setShowSecondChance: Dispatch<SetStateAction<boolean>>;
	triggerSecondChance: () => boolean;
	handleSecondChance: () => void;
}

export interface UseFastAnsweringProps {
	userData: UserData;
	currentQuestion: CurrentQuestion;
	exerciseTypes: ExerciseTypes;
	viewMode: ViewMode;
	preparedMultipleAnswers: PreparedMultipleAnswers;
	setPreparedMultipleAnswers: Dispatch<SetStateAction<PreparedMultipleAnswers>>;
	handleUserAnswer: (question: CurrentQuestion) => void;
}

export interface UseFastAnsweringResult {
	showFastAnswering: boolean;
	setShowFastAnswering: Dispatch<SetStateAction<boolean>>;
	triggerFastAnswering: () => boolean;
	resetFastAnswering: () => void;
	handleFastAnswering: () => void;
	handleClickFastAnswering: () => void;
}

export interface QuestionQueue {
	getRemainQuestionsLength: () => number;
}

export interface UseReplayProps {
	currentQuestion: CurrentQuestion;
	exerciseTypes: ExerciseTypes;
	viewMode: ViewMode;
	preparedMultipleAnswers: PreparedMultipleAnswers;
	setPreparedMultipleAnswers: Dispatch<SetStateAction<PreparedMultipleAnswers>>;
	setPreparedBlanksMultipleAnswers: Dispatch<SetStateAction<Record<number, any>>>;
	setPressCheck: (flag: boolean) => void;
	setExerciseScore: (score: number, penalty: number, total: number) => void;
	setUserSingleAnswer: (answer: string) => void;
	setIsAnswerShowing: (flag: boolean) => void;
	questionQueue: QuestionQueue;
	handleUserAnswer: (question: CurrentQuestion) => void;
}

export interface UseReplayResult {
	showReplayBottomBar: boolean;
	handleCheckReplayAnswer: () => void;
	handleReplayAnswer: () => void;
	handleShowUserAnswers: (showAnswers: boolean) => void;
}

// ============================================================================
// Component Props Types
// ============================================================================

export interface QuestionHeaderProps {
	currentQuestion?: Question;
	instruction?: Record<string, any> | null;
	showInstructionHint?: boolean;
	showQuestionInstruction?: boolean;
	showTopicInstruction?: boolean;
	toggleQuestionInstruction?: (flag: boolean) => void;
	toggleTopicInstruction?: (flag: boolean) => void;
}

export interface QuestionTitleProps {
	currentQuestion?: Question;
	onClickTitle?: () => void;
	showPulsatingHighlight?: boolean;
}

export interface MistakeInterventionProps {
	loading: boolean;
	mistakeInterventionId: string | number;
	resetMistakeIntervention: (id: string | number) => void;
	sendEventRequest: (payload: { event_name: string; meta_data: Record<string, any> }) => void;
	sendMistakeIntervention: (playlistId: string, knowledgeComponentId: string | number) => void;
	setMistakeIntervention: (mistakeInterventionId: number | null) => void;
}

export interface AiTutorFeedbackProps {
	currentQuestion: Question;
	aiTutorFeedbacks: Record<string, string>;
	sendAiTutorFeedback: (feedback: string, questionId: number | string) => void;
	sendEventRequest: (params: { event_name: string; meta_data: Record<string, any> }) => void;
	aiTutorFeedbackOpen: boolean;
	toggleAiTutorFeedback: (isOpen: boolean) => void;
}

// ============================================================================
// Legacy Compatibility Types (for backward compatibility during migration)
// ============================================================================

export type GameAnswer = Answer;

export interface GameState {
	loading: boolean;
	currentLevel?: CurrentLevel | null;
	playList?: PlayList | null;
	questions: Question[];
}

export interface ExerciseState {
	audioOn: boolean;
	correctCount: number;
	correctStreak: number;
	currentQuestion: any;
	datePlayed: string | null;
	endscreen: any;
	exerciseTypes: ExerciseTypes;
	id: string | number | null;
	incorrectCount: number;
	instruction: Record<string, any> | null;
	instructionAudioOn: boolean;
	instructionFeedbacks: Record<string, boolean>;
	isReadingMode: boolean;
	levelUp: any;
	loading: boolean;
	loadingAnswer: boolean;
	loadingFinish: boolean;
	loadingMistakeIntervention: boolean;
	playlist_section_text: Record<string, any>;
	questions: any[];
	questionsLeftCount: number;
	questionsExplanations: Record<string, any>;
	readingProgress: number;
	readingTextSize: number;
	section_type: string | null;
	showInstructionHint: boolean;
	showPulsatingHighlight: boolean;
	showQuestionInstruction: boolean;
	showReadingTitle: boolean;
	showTopicInstruction: boolean;
	topic_subcategory_name: string | null;
	topic_title: string | null;
	viewMode: string | null;
	mistakeInterventionId: string | number | null;
	showAITutorChat: boolean;
	freePlayTheme: any;
	aiTutorFeedbacks: Record<string, any>;
	aiTutorFeedbackOpen: boolean;
}

export type ExerciseActionPayload = Partial<{
	audioOn: boolean;
	correctCount: number;
	correctStreak: number;
	currentQuestion: any;
	data: any;
	endscreen: any;
	exerciseType: string;
	incorrectCount: number;
	instructionAudioOn: boolean;
	instructionId: string | number;
	isReadingMode: boolean;
	isUseful: boolean;
	levelUp: any;
	questionId: string | number;
	questionsLeftCount: number;
	questionsExplanations: Record<string, any>;
	readingProgress: number;
	readingTextSize: number;
	showInstructionHint: boolean;
	showPulsatingHighlight: boolean;
	showQuestionInstruction: boolean;
	showReadingTitle: boolean;
	showTopicInstruction: boolean;
	mistakeInterventionId: string | number;
	viewMode: string;
	aiTutorFeedbackOpen: boolean;
	feedback: any;
}>;

export interface ExerciseAction {
	type: string;
	payload?: ExerciseActionPayload;
}
